import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { generateSampleQuestions } from '../data/sampleQuestions';
import { detectSubjectFromTopic, validateGeneratedQuestions, preValidateRequest } from '../utils/quizValidation';
import { normalizeQuestionType } from '../utils/questionTypeNormalization';

/**
 * Detect duplicate questions by comparing normalized stems
 * @param questions - Array of questions
 * @returns Array of duplicate indices
 */
function detectDuplicates(questions: any[]): number[] {
  const duplicates: number[] = [];
  const seen = new Map<string, number>();

  questions.forEach((q, index) => {
    // Normalize: lowercase, remove punctuation, trim
    const normalized = q.text
      .toLowerCase()
      .replace(/[.,!?;:]/g, '')
      .trim();

    if (seen.has(normalized)) {
      duplicates.push(index);
    } else {
      seen.set(normalized, index);
    }
  });

  return duplicates;
}

/**
 * Calculate type distribution to ensure all selected types are represented
 * Returns a map of type -> minimum count
 */
function calculateTypeDistribution(totalQuestions: number, selectedTypes: string[]): Record<string, number> {
  const distribution: Record<string, number> = {};

  if (selectedTypes.length === 0 || totalQuestions === 0) {
    return distribution;
  }

  // CRITICAL FIX: Respect totalQuestions limit even when more types selected
  // Priority order: auto-graded types first, then manual types
  const autoGradedTypes = ['multiple_choice', 'true_false', 'fill_blank', 'matching', 'multiple_select'];
  const manualTypes = ['short_answer', 'essay'];

  // Sort types: auto-graded first, then manual
  const sortedTypes = [
    ...selectedTypes.filter(t => autoGradedTypes.includes(t)),
    ...selectedTypes.filter(t => manualTypes.includes(t))
  ];

  if (sortedTypes.length === 0) {
    return distribution;
  }

  // If more types than questions, prioritize auto-graded and limit to available slots
  if (sortedTypes.length > totalQuestions) {
    // Give 1 question to first N types (prioritized order)
    sortedTypes.slice(0, totalQuestions).forEach(type => {
      distribution[type] = 1;
    });
  } else {
    // Give each type at least 1 question
    const minPerType = 1;
    const reservedQuestions = sortedTypes.length * minPerType;
    const remaining = totalQuestions - reservedQuestions;
    const extraPerType = Math.floor(remaining / sortedTypes.length);
    const leftover = remaining % sortedTypes.length;

    sortedTypes.forEach((type, index) => {
      distribution[type] = minPerType + extraPerType + (index < leftover ? 1 : 0);
    });
  }

  // Final safety: ensure total doesn't exceed requested
  const actualTotal = Object.values(distribution).reduce((sum, count) => sum + count, 0);
  if (actualTotal !== totalQuestions) {
    console.warn(`⚠️ Distribution mismatch: ${actualTotal} vs ${totalQuestions} requested. Adjusting...`);
    // Force exact count by trimming excess from last type
    const types = Object.keys(distribution);
    const excess = actualTotal - totalQuestions;
    if (excess > 0 && types.length > 0) {
      const lastType = types[types.length - 1];
      distribution[lastType] = Math.max(0, distribution[lastType] - excess);
    }
  }

  console.log(`📊 Question Distribution Plan for ${totalQuestions} questions:`, distribution);
  return distribution;
}

/**
 * Validate that all selected types are present in generated questions
 * Returns missing types and type counts
 * ✅ PHASE 4: Normalize both sides to fix hyphen/underscore mismatch
 */
function validateTypeDistribution(
  questions: any[],
  selectedTypes: string[]
): { missingTypes: string[]; typeCounts: Record<string, number> } {
  const typeCounts: Record<string, number> = {};

  // ✅ Count questions by normalized type (fixes multiple-choice vs multiple_choice)
  questions.forEach(q => {
    const normalizedType = normalizeQuestionType(q.type) || 'unknown';
    typeCounts[normalizedType] = (typeCounts[normalizedType] || 0) + 1;
  });

  // ✅ Normalize requested types before comparison
  const normalizedSelectedTypes = selectedTypes.map(t => normalizeQuestionType(t) || t);

  // Find missing types (now both sides use hyphenated format)
  const missingTypes = normalizedSelectedTypes.filter(type => !typeCounts[type] || typeCounts[type] === 0);

  return { missingTypes, typeCounts };
}

/**
 * Clean MCQ question stem by removing embedded answer choices
 * @param stem - Question text
 * @param choices - Array of choice objects
 * @returns Cleaned question stem
 */
function cleanQuestionStem(stem: string, choices?: Array<{id: string, text: string, isCorrect: boolean}>): string {
  if (!choices || choices.length === 0 || !stem) {
    return stem;
  }

  let cleanedStem = stem.trim();

  // Pattern 1: Remove trailing "A. ... B. ... C. ... D. ..." patterns
  // Match patterns like:
  // "Question text? A. Choice1 B. Choice2 C. Choice3 D. Choice4"
  // "Question text? A) Choice1 B) Choice2 C) Choice3 D) Choice4"
  const choicePattern = /[\s\n]+([A-Z][\.\)])\s+[^\n]+/g;
  const matches = cleanedStem.match(choicePattern);

  if (matches && matches.length >= 2) {
    // Check if these matches correspond to actual choices
    const potentialChoices = matches.map(m => m.trim());
    const actualChoiceTexts = choices.map(c => c.text.toLowerCase().trim());

    let hasMatch = false;
    for (const pc of potentialChoices) {
      const choiceText = pc.replace(/^[A-Z][\.\)]\s+/, '').toLowerCase().trim();
      if (actualChoiceTexts.some(act => act === choiceText || act.startsWith(choiceText) || choiceText.startsWith(act))) {
        hasMatch = true;
        break;
      }
    }

    if (hasMatch) {
      // Remove everything after the first choice marker
      cleanedStem = cleanedStem.split(/[\s\n]+[A-Z][\.\)]\s+/)[0].trim();
    }
  }

  return cleanedStem;
}

interface BuilderState {
  title: string;
  sourceMaterial?: string; // Lesson notes/content pasted by teacher
  gradeLevel: string;
  examBoard?: string;
  count: number;
  standardsText: string;
  promptBlocks: string[];
  webSearchEnabled: boolean;
  questionTypes?: string[];
  cognitiveLevel?: string[];
  // Enhanced options for multi-part questions
  difficulty?: 'Easy' | 'Medium' | 'Hard' | 'Mixed';
  subject?: string;
  includeDiagrams?: boolean;
  preventDuplicates?: boolean;
  googleFormsMode?: boolean;
}

interface GeneratedQuestion {
  id: string;
  type: 'mcq' | 'true_false' | 'short' | 'multipart' | 'matching' | 'multiple_select';
  text: string;
  choices?: Array<{
    id: string;
    text: string;
    isCorrect: boolean;
  }> | null;
  correctAnswer?: string | boolean | null;
  correctAnswers?: string[]; // For multiple select
  points: number;
  explanation?: string;
  // Multi-part question support
  parts?: Array<{
    key: string;
    prompt: string;
    marks: number;
    type: 'text' | 'number' | 'mcq';
    answer?: string;
    numeric?: { value: number; tolerance?: number; sigFigs?: number };
    choices?: Array<{ id: string; text: string; isCorrect: boolean }>;
  }>;
  totalMarks?: number;
  context?: string;
}

interface AIServiceResponse {
  questions: GeneratedQuestion[];
}

/**
 * Build a comprehensive prompt from the builder state
 */
export function buildPrompt(builderState: BuilderState): string {
  const {
    title,
    gradeLevel,
    examBoard = 'General Curriculum',
    standardsText,
    promptBlocks,
    webSearchEnabled,
    questionTypes = ['multiple_choice'],
    cognitiveLevel = ['understanding'],
    difficulty = 'Medium',
    subject = 'General',
    includeDiagrams = false,
    preventDuplicates = true,
    googleFormsMode = false
  } = builderState;

  // CRITICAL: Auto-detect subject from topic and prioritize topic
  const detectedSubject = detectSubjectFromTopic(title);
  const finalSubject = detectedSubject || subject;

  // Log if subject was auto-corrected
  if (detectedSubject && detectedSubject !== subject) {
    console.log(`📚 Subject auto-corrected: ${subject} → ${detectedSubject} (based on topic "${title}")`);
  }

  let prompt = `Create a comprehensive ${gradeLevel} level assessment about "${title}"`;

  // Prioritize topic over subject
  if (title && finalSubject !== 'General') {
    prompt += ` in the context of ${finalSubject}`;
  }

  // Add exam board specificity
  if (examBoard && examBoard !== 'General Curriculum') {
    prompt += `, aligned with ${examBoard} curriculum standards`;
  }

  // CRITICAL: Topic-first approach
  prompt += `\n\n**PRIMARY FOCUS: "${title}"**`;
  prompt += `\nSubject Area: ${finalSubject}`;
  prompt += `\n\n⚠️ CRITICAL: ALL questions must be directly about "${title}". DO NOT mix topics from other subjects.`;
  prompt += `\nExample: If topic is "elements", only ask about chemical elements, NOT physics forces or biology cells.`;

  if (standardsText) {
    prompt += `\n\nAdditional requirements: ${standardsText}`;
  }

  // Google Forms Mode - Override question types and add specific instructions
  if (googleFormsMode) {
    prompt += `\n\n**GOOGLE FORMS MODE - CRITICAL REQUIREMENTS:**
This quiz will be exported to Google Forms. You MUST follow these rules strictly:

1. **ONLY generate these question types:**
   - 60% Multiple Choice (4 options, single correct answer) - AUTO-GRADED
   - 20% True/False (clear definitive statements) - AUTO-GRADED
   - 20% Essay/Paragraph questions (open-ended, requires analysis) - MANUALLY GRADED

2. **For Multiple Choice questions:**
   - Provide exactly 4 options (A, B, C, D)
   - Only ONE option is correct
   - Make distractors plausible but clearly incorrect
   - Avoid "All of the above" or "None of the above"

3. **For True/False questions:**
   - Make statements clear and unambiguous
   - Answer must be definitively true OR false (no "sometimes" or "it depends")

4. **For Essay/Paragraph questions:**
   - Ask open-ended questions that require critical thinking, analysis, or explanation
   - Use prompts like "Explain...", "Describe...", "Compare and contrast...", "Analyze..."
   - These will be manually graded by the teacher in Google Forms
   - Provide sample answer or grading rubric in "explanation" field for teacher reference
   - Assign 3-5 points for essay questions (more complex than auto-graded questions)
   - Example: "Explain the process of photosynthesis and why it is important for life on Earth"

5. **Answer Key Requirements:**
   - For MCQ/True/False: Store correct answers clearly in the response
   - For Essays: Provide a sample answer or key points in the "explanation" field
   - MCQ and True/False: 1 point each
   - Essay questions: 3-5 points each (based on complexity)

6. **Question Distribution:**
   - If generating 10 questions: 6 MCQ, 2 True/False, 2 Essay
   - If generating 5 questions: 3 MCQ, 1 True/False, 1 Essay
   - Maintain this 60/20/20 ratio across all quiz sizes

Generate questions using ONLY these Google Forms compatible types:`;
  } else {
    // Normal mode - Dynamic question types based on selection
    prompt += `\n\nGenerate questions using these specific question types:`;
  }

  const questionTypeDescriptions = {
    multiple_choice: '- Multiple choice questions (with 4 options A, B, C, D)',
    true_false: '- True/False questions (with clear statements that are definitively true or false)',
    short_answer: '- Short answer questions (requiring 1-3 sentence responses)',
    fill_blank: '- Fill in the blank questions (complete sentences with missing key terms WITH 3-4 dropdown options)',
    matching: '- Matching questions (connect physics terms with their correct definitions)',
    multiple_select: '- Multiple select questions (choose all correct answers from options)'
  };

  // In Google Forms mode, override question types
  if (googleFormsMode) {
    prompt += `\n- Multiple choice questions (60% of total)`;
    prompt += `\n- True/False questions (20% of total)`;
    prompt += `\n- Essay/Paragraph questions (20% of total)`;
  } else {
    questionTypes.forEach(type => {
      if (questionTypeDescriptions[type]) {
        prompt += `\n${questionTypeDescriptions[type]}`;
      }
    });
  }

  prompt += `\n\n**Question Distribution:**
- Distribute questions evenly across selected types
- Balance cognitive levels (recall, understanding, application, analysis)
- Use clear, student-friendly language appropriate for ${gradeLevel}
- Include real-world examples when relevant`;

  // Add exam board specific guidance
  if (examBoard.includes('GCSE') || examBoard.includes('A-Level') || examBoard.includes('IGCSE')) {
    prompt += `\n- Follow ${examBoard} examination style and marking criteria`;
  } else if (examBoard.includes('IB')) {
    prompt += `\n- Follow IB assessment criteria and command terms`;
  } else if (examBoard.includes('AP')) {
    prompt += `\n- Follow AP exam format and College Board standards`;
  }

  // Bloom's Taxonomy cognitive levels
  if (cognitiveLevel.length > 0) {
    prompt += `\n\n**Bloom's Taxonomy Target Levels:**`;

    const levelDescriptions = {
      remembering: 'Remembering - Recall facts, terms, basic concepts (define, list, identify, state)',
      understanding: 'Understanding - Explain ideas, comprehend meaning (explain, describe, summarize, interpret)',
      applying: 'Applying - Use information in new situations (apply, solve, demonstrate, calculate)',
      analyzing: 'Analyzing - Break down information, examine relationships (analyze, compare, contrast, examine)',
      evaluating: 'Evaluating - Make judgments, critique (evaluate, assess, justify, critique)',
      creating: 'Creating - Produce new work, generate ideas (create, design, construct, plan)'
    };

    cognitiveLevel.forEach(level => {
      if (levelDescriptions[level as keyof typeof levelDescriptions]) {
        prompt += `\n- ${levelDescriptions[level as keyof typeof levelDescriptions]}`;
      }
    });

    prompt += `\n\nIMPORTANT: Distribute questions across these cognitive levels. Each question must clearly align with one of the selected levels:
- LABEL each question with its Bloom's level (e.g., "Level 2 - Understanding:")
- Use appropriate command verbs for each level
- Ensure questions progressively increase in complexity
- Balance the distribution across selected levels`;
  }

  // Difficulty specification
  prompt += `\n\nDifficulty level: ${difficulty}`;
  if (difficulty === 'Mixed') {
    prompt += ` (vary difficulty across questions from easy to hard)`;
  }

  // Subject-specific instructions (using auto-detected subject)
  if (finalSubject === 'Science' || finalSubject === 'Physics') {
    prompt += `\n\nFor ${finalSubject.toLowerCase()} questions about "${title}":\n- Include relevant formulas and equations\n- Use proper scientific units\n- Include numerical calculations with significant figures\n- Reference scientific principles and laws\n- For matching questions: use physics terms like "Force", "Gravity", "Friction", "Magnetism" with their correct physics definitions\n- Ensure all content is scientifically accurate and age-appropriate`;
  } else if (finalSubject === 'Mathematics') {
    prompt += `\n\nFor mathematics questions about "${title}":\n- Include step-by-step problem solving\n- Show mathematical reasoning\n- Use appropriate mathematical notation\n- Include both computational and conceptual questions`;
  } else if (finalSubject === 'Chemistry') {
    prompt += `\n\nFor chemistry questions about "${title}":\n- Include chemical formulas and equations\n- Use proper chemical nomenclature and symbols\n- Reference the periodic table when relevant\n- Include concepts like atomic structure, chemical reactions, bonding, etc.\n- Ensure all content relates specifically to "${title}"`;
  } else if (finalSubject === 'Biology') {
    prompt += `\n\nFor biology questions about "${title}":\n- Include biological processes and systems\n- Use proper scientific terminology\n- Reference cellular, molecular, or organismal concepts as appropriate\n- Include real-world biological examples\n- Ensure all content relates specifically to "${title}"`;
  } else if (finalSubject === 'History') {
    prompt += `\n\nFor history questions:\n- Include historical context and timelines\n- Focus on cause-and-effect relationships\n- Include primary source analysis when relevant\n- Test both factual knowledge and analytical thinking`;
  }

  // Template format instructions (skip multi-part in Google Forms mode)
  if (!googleFormsMode) {
    prompt += '\n\nFormat questions as multi-part with clear context paragraphs and progressive difficulty from basic recall to application.';
  } else {
    prompt += '\n\nFormat each question as a standalone single-part question. Do NOT create multi-part questions.';
  }

  // Visual elements
  if (includeDiagrams) {
    prompt += `\n\n- Include descriptions for relevant diagrams, charts, or visual elements where appropriate`;
  }

  // Quality controls
  if (preventDuplicates) {
    prompt += `\n\n- Ensure all questions test different concepts and avoid repetition`;
  }
  
  // Add custom prompt blocks
  if (promptBlocks.length > 0) {
    const validBlocks = promptBlocks.filter(block => block.trim().length > 0);
    if (validBlocks.length > 0) {
      prompt += `\n\nAdditional requirements:\n${validBlocks.map(block => `- ${block}`).join('\n')}`;
    }
  }
  
  if (webSearchEnabled) {
    prompt += '\n\nInclude current, real-world examples and recent developments where relevant.';
  }
  
  return prompt.trim();
}

/**
 * Validate and fix questions for Google Forms compatibility
 * Ensures all fill-in-blank questions have options for dropdown export
 */
function validateGoogleFormsQuestions(
  questions: GeneratedQuestion[],
  builderState: BuilderState
): GeneratedQuestion[] {
  return questions.map((q, index) => {
    // Check if it's a fill-in-blank question
    if (q.type === 'fill_blank' || q.type === 'fillBlank') {
      // Check if it has options
      const hasOptions = q.choices && Array.isArray(q.choices) && q.choices.length > 0;

      if (!hasOptions) {
        console.warn(`⚠️ Fill-in-blank question ${index + 1} missing options. Generating fallback options...`);

        // Get the correct answer
        const correctAnswer = q.correctAnswer || q.text?.match(/\b[A-Z][a-z]+\b/)?.[0] || 'Answer';

        // Generate simple distractors based on subject
        const subject = builderState.subject?.toLowerCase() || 'general';
        let distractors: string[] = [];

        if (subject.includes('biology') || subject.includes('science')) {
          distractors = ['Nucleus', 'Mitochondria', 'Cytoplasm', 'Ribosome', 'Cell Membrane', 'Chloroplast'];
        } else if (subject.includes('physics')) {
          distractors = ['Force', 'Energy', 'Mass', 'Velocity', 'Acceleration', 'Momentum'];
        } else if (subject.includes('chemistry')) {
          distractors = ['Atom', 'Molecule', 'Electron', 'Proton', 'Neutron', 'Ion'];
        } else if (subject.includes('math')) {
          distractors = ['Variable', 'Constant', 'Coefficient', 'Equation', 'Function', 'Expression'];
        } else {
          distractors = ['Option A', 'Option B', 'Option C', 'Option D'];
        }

        // Filter out the correct answer from distractors and pick 3
        const filteredDistractors = distractors
          .filter(d => d.toLowerCase() !== correctAnswer.toLowerCase())
          .slice(0, 3);

        // Create options array with correct answer + distractors
        const options = [correctAnswer, ...filteredDistractors];

        // Shuffle options
        const shuffledOptions = options.sort(() => Math.random() - 0.5);

        // Convert to choices format AND add options array for export
        q.choices = shuffledOptions.map((opt, idx) => ({
          id: String.fromCharCode(65 + idx), // A, B, C, D
          text: opt,
          isCorrect: opt === correctAnswer
        }));

        // Also set options array directly for export compatibility
        (q as any).options = shuffledOptions;

        console.log(`✓ Generated options for fill-in-blank: ${shuffledOptions.join(', ')}`);
      }
    }

    return q;
  });
}

/**
 * Generate quiz questions using the existing OpenAI Cloud Function
 */
export async function generateMCQs(
  builderState: BuilderState
): Promise<GeneratedQuestion[]> {
  try {
    // 🔍 PRE-VALIDATION: Check request validity BEFORE calling OpenAI
    const preValidation = preValidateRequest({
      title: builderState.title,
      count: builderState.count,
      questionTypes: builderState.questionTypes,
      subject: builderState.subject
    });

    // Block CRITICAL issues (empty topic, invalid count, etc.)
    const criticalIssues = preValidation.issues.filter(i => i.severity === 'CRITICAL');
    if (criticalIssues.length > 0) {
      const errorMessage = criticalIssues.map(issue => issue.message).join(' ');
      throw new Error(errorMessage);
    }

    // Log warnings for LOW/MEDIUM issues (don't block)
    const warnings = preValidation.issues.filter(i => i.severity !== 'CRITICAL');
    if (warnings.length > 0) {
      console.warn('⚠️ Pre-validation warnings:');
      warnings.forEach(w => console.warn(`  [${w.severity}] ${w.message}`));
    }

    // Configure with 5 minute timeout to handle large quiz generation
    const generateQuizQuestions = httpsCallable(functions, 'generateQuizQuestions', {
      timeout: 300000 // 5 minutes (300 seconds)
    });

    // Auto-detect subject from topic
    const detectedSubject = detectSubjectFromTopic(builderState.title);
    const finalSubject = detectedSubject || builderState.subject || 'General';

    // Build the topic from our comprehensive prompt
    const topic = buildPrompt(builderState);

    // Map grade level to difficulty
    const difficultyMap: Record<string, string> = {
      'Elementary (K-5)': 'easy',
      'Middle School (6-8)': 'medium',
      'High School (9-12)': 'hard',
      'College/University': 'hard'
    };

    const difficulty = difficultyMap[builderState.gradeLevel] || 'medium';

    // Map our UI question types to what the cloud function expects
    const questionTypeMapping = {
      'multiple_choice': 'multiple_choice',
      'true_false': 'true_false',
      'short_answer': 'short_answer',
      'fill_blank': 'fill_blank',
      'matching': 'matching',
      'multiple_select': 'multiple_select'
    };

    const mappedQuestionTypes = (builderState.questionTypes || ['multiple_choice'])
      .map(type => questionTypeMapping[type as keyof typeof questionTypeMapping] || type);

    // Calculate type distribution to ensure all selected types are represented
    const typeDistribution = calculateTypeDistribution(builderState.count, mappedQuestionTypes);

    // ✅ Generate idempotency key to prevent double-charging on retries
    const requestId = `ai_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;

    console.log('🔧 Calling AI service with parameters:', {
      requestId,
      numQuestions: builderState.count,
      questionTypes: mappedQuestionTypes,
      typeDistribution, // Show calculated distribution
      cognitiveLevel: builderState.cognitiveLevel,
      examBoard: builderState.examBoard,
      subject: finalSubject,
      autoDetected: detectedSubject ? `Yes (${builderState.subject} → ${detectedSubject})` : 'No'
    });

    const result = await generateQuizQuestions({
      requestId, // ✅ Idempotency key prevents double-charging
      topic,
      sourceMaterial: builderState.sourceMaterial?.trim() || null, // Teacher-provided lesson content
      numQuestions: builderState.count, // Support up to 50 questions
      difficulty,
      includeDiagrams: builderState.includeDiagrams || false,
      questionTypes: mappedQuestionTypes,
      typeDistribution, // Pass distribution to guide AI generation
      cognitiveLevel: builderState.cognitiveLevel || ['understanding'],
      examBoard: builderState.examBoard || 'General Curriculum',
      subject: finalSubject, // Use auto-detected subject
      yearLevel: builderState.gradeLevel || 'Year 10' // Pass grade level for vocabulary adjustment
    });

    if (!result.data?.questions) {
      throw new Error('No questions returned from AI service');
    }

    // Transform the response to match our expected format
    const transformedQuestions = result.data.questions.map((q: any, index: number) => {
      // Normalize question type using central utility
      // This ensures consistent format across AI Builder, previews, and exam player
      let questionType = normalizeQuestionType(q.type) || 'multiple-choice';

      // Auto-detect True/False questions by content
      const questionText = q.text || q.question || '';
      const correctAnswer = q.correctAnswer || q.answer;

      // If the question text ends with a statement and answer is boolean or "True"/"False"
      if (questionType !== 'true-false' &&
          (correctAnswer === true || correctAnswer === false ||
           correctAnswer === 'True' || correctAnswer === 'False' ||
           correctAnswer === 'true' || correctAnswer === 'false')) {
        questionType = 'true-false';
      }

      // Auto-detect Fill-in-the-blank by presence of blanks
      if (questionType !== 'fill-blank' && questionText.includes('________')) {
        questionType = 'fill-blank';
      }

      // Clean MCQ/Multiple Select question stems to remove embedded choices
      const cleanedText = (questionType === 'multiple-choice' || questionType === 'multiple-select')
        ? cleanQuestionStem(questionText, q.choices)
        : questionText;

      return {
        id: q.id || `q_${Date.now()}_${index}`,
        type: questionType,
        text: cleanedText,
        choices: q.choices || (questionType === 'multiple-choice' || questionType === 'multiple-select' ? [
          { id: 'A', text: '', isCorrect: false },
          { id: 'B', text: '', isCorrect: false },
          { id: 'C', text: '', isCorrect: false },
          { id: 'D', text: '', isCorrect: false }
        ] : questionType === 'true-false' ? [
          {
            id: 'A',
            text: 'True',
            isCorrect: correctAnswer === true || correctAnswer === 'True' || correctAnswer === 'true'
          },
          {
            id: 'B',
            text: 'False',
            isCorrect: correctAnswer === false || correctAnswer === 'False' || correctAnswer === 'false'
          }
        ] : null),
        correctAnswer: q.correctAnswer || q.answer,
        correctAnswers: q.correctAnswers || [], // For multiple select
        // Multi-part question support
        parts: q.parts || [],
        totalMarks: q.totalMarks || q.points || 1,
        context: q.context || null,
        points: q.points || 1,
        explanation: q.explanation || '',
        // Stage 1 additions
        bloomLevel: q.bloomLevel || 'understanding',
        bloomJustification: q.bloomJustification || null,
        difficulty: q.difficulty || null,
        requiresManualGrading: q.requiresManualGrading || false
      };
    });

    console.log('🔧 Transformed questions:', transformedQuestions.map(q => ({ id: q.id, type: q.type, text: q.text.substring(0, 50) + '...' })));

    // Detect duplicates and warn (don't remove, let teacher decide)
    const duplicateIndices = detectDuplicates(transformedQuestions);
    if (duplicateIndices.length > 0) {
      console.warn(`⚠️ Found ${duplicateIndices.length} potential duplicate questions at indices: ${duplicateIndices.join(', ')}`);
      // Mark duplicates with a flag for UI warning
      duplicateIndices.forEach(idx => {
        if (transformedQuestions[idx]) {
          (transformedQuestions[idx] as any)._isDuplicate = true;
        }
      });
    }

    // ✅ PHASE 6: Backend enforces distribution - DO NOT retry per missing type
    // Frontend validation is for error display only
    const { missingTypes, typeCounts } = validateTypeDistribution(transformedQuestions, mappedQuestionTypes);

    console.log('📊 Type distribution received:', typeCounts);

    if (missingTypes.length > 0) {
      // Backend should have enforced distribution - this indicates a backend issue
      console.error(`❌ Backend returned incomplete type distribution:`, {
        requested: mappedQuestionTypes,
        missing: missingTypes,
        received: typeCounts
      });

      // Show clear error to user (backend validation gate should prevent this)
      throw new Error(
        `AI generation incomplete: missing question types (${missingTypes.join(', ')}). ` +
        `This should not happen. Please try again or contact support if it persists.`
      );
    }

    console.log(`✅ Received ${transformedQuestions.length} questions (requested: ${builderState.count})`);

    // ✅ PHASE 7: Defensive exact count enforcement
    let finalQuestions = transformedQuestions;
    if (transformedQuestions.length !== builderState.count) {
      console.warn(`⚠️ Count mismatch: received ${transformedQuestions.length}, requested ${builderState.count}`);

      if (transformedQuestions.length > builderState.count) {
        // Too many - trim to exact count (backend should have handled this)
        console.log(`Trimming to exact count: ${builderState.count}`);
        finalQuestions = transformedQuestions.slice(0, builderState.count);
      } else {
        // Too few - this is an error (backend should have enforced exact count)
        throw new Error(
          `AI returned only ${transformedQuestions.length} questions (requested ${builderState.count}). ` +
          `Backend validation failed. Please try again.`
        );
      }
    }

    // Validate and fix questions for Google Forms Mode
    const validatedQuestions = builderState.googleFormsMode
      ? validateGoogleFormsQuestions(finalQuestions, builderState)
      : finalQuestions;

    // Run quality validation on generated questions
    const validationReport = validateGeneratedQuestions(
      validatedQuestions.map(q => ({
        id: q.id,
        stem: q.text,
        text: q.text,
        options: q.choices?.map(c => c.text) || [],
        correctIndex: q.choices?.findIndex(c => c.isCorrect) || 0,
        explanation: q.explanation
      })),
      builderState.title,
      finalSubject
    );

    // Log validation results
    console.log('📊 Validation Report:', {
      passed: validationReport.passed,
      score: validationReport.score,
      issueCount: validationReport.issues.length,
      detectedSubject: validationReport.detectedSubject,
      subjectMismatch: validationReport.subjectMismatch
    });

    if (validationReport.issues.length > 0) {
      console.warn('⚠️ Validation Issues Found:');
      validationReport.issues.forEach(issue => {
        console.warn(`  [${issue.severity}] ${issue.type}: ${issue.message}`);
        if (issue.question) {
          console.warn(`    Question: "${issue.question}..."`);
        }
      });
    }

    // Attach validation report to questions for UI display
    (validatedQuestions as any).validationReport = validationReport;

    return validatedQuestions;

  } catch (error) {
    console.error('AI service error:', error);

    // Log detailed error information for debugging
    if (error && typeof error === 'object') {
      console.error('🔍 Detailed error:', {
        name: (error as any).name,
        message: (error as any).message,
        code: (error as any).code,
        details: (error as any).details,
        customData: (error as any).customData,
        fullError: error
      });
    }

    // Extract user-friendly error message
    let errorMessage = 'Unknown error';
    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (error && typeof error === 'object' && 'message' in error) {
      errorMessage = String((error as any).message);
    }

    // Check if it's a Firebase error with more details
    if (error && typeof error === 'object' && 'code' in error) {
      const firebaseError = error as any;

      // 🔴 PERMANENT ERRORS - Don't retry, show clear message
      if (firebaseError.code === 'functions/unauthenticated') {
        errorMessage = 'Please sign in to use AI features';
      } else if (firebaseError.code === 'functions/permission-denied') {
        errorMessage = firebaseError.message || 'Permission denied. Please verify your email to use AI features.';
      } else if (firebaseError.code === 'functions/resource-exhausted') {
        errorMessage = firebaseError.message || 'AI usage limit reached';
      } else if (firebaseError.code === 'functions/failed-precondition') {
        errorMessage = firebaseError.message || 'AI service configuration error';
      } else if (firebaseError.code === 'functions/invalid-argument') {
        errorMessage = firebaseError.message || 'Invalid request. Please check your quiz settings.';
      }
      // ⚠️ TEMPORARY ERRORS - Could be retried (but we don't auto-retry to save costs)
      else if (firebaseError.code === 'functions/deadline-exceeded') {
        // PR-B1: Improved deadline guidance
        errorMessage = `Generation took too long and timed out. For faster results:\n• Use 10 questions instead of 20+\n• Remove essay questions\n• Use 1-3 question types\n• Focus on one specific topic`;
      } else if (firebaseError.code === 'functions/unavailable') {
        errorMessage = 'AI service is temporarily unavailable. Please try again in a moment.';
      } else if (firebaseError.code === 'functions/internal') {
        errorMessage = firebaseError.message || 'AI service error. Please try again.';
      }
    }

    // Re-throw with enhanced error message
    throw new Error(`AI service failed: ${errorMessage}`);
  }
}

/**
 * Regenerate a single question in an existing quiz
 * @param questionIndex - Index of the question to regenerate (0-based)
 * @param existingQuestions - All questions in the quiz
 * @param builderState - Original quiz parameters
 */
export async function regenerateSingleQuestion(
  questionIndex: number,
  existingQuestions: any[],
  builderState: BuilderState
): Promise<GeneratedQuestion> {
  try {
    const regenerateQuestion = httpsCallable(functions, 'regenerateSingleQuestion');

    // Auto-detect subject
    const detectedSubject = detectSubjectFromTopic(builderState.title);
    const finalSubject = detectedSubject || builderState.subject || 'General';

    // Map grade level to difficulty
    const difficultyMap: Record<string, string> = {
      'Elementary (K-5)': 'easy',
      'Middle School (6-8)': 'medium',
      'High School (9-12)': 'hard',
      'College/University': 'hard'
    };

    const difficulty = difficultyMap[builderState.gradeLevel] || 'medium';

    // Get the question type of the question being regenerated (if available)
    const questionType = existingQuestions[questionIndex]?.type || null;

    console.log(`🔄 Regenerating question ${questionIndex + 1}...`);

    const result = await regenerateQuestion({
      topic: builderState.title,
      questionIndex,
      existingQuestions: existingQuestions.map(q => ({
        text: q.text,
        type: q.type
      })),
      questionType,
      difficulty,
      subject: finalSubject,
      yearLevel: builderState.gradeLevel || 'Year 10',
      cognitiveLevel: builderState.cognitiveLevel || ['understanding'],
      examBoard: builderState.examBoard || 'General Curriculum'
    });

    if (!result.data?.question) {
      throw new Error('No question returned from regeneration service');
    }

    // Transform the response to match our expected format
    const q = result.data.question;

    // Normalize type using central utility
    let finalType = normalizeQuestionType(q.type) || 'multiple-choice';

    const transformedQuestion: GeneratedQuestion = {
      id: q.id || `q_regen_${Date.now()}`,
      type: finalType as any,
      text: q.text || '',
      choices: q.choices || null,
      correctAnswer: q.correctAnswer || null,
      correctAnswers: q.correctAnswers || [],
      points: q.points || 1,
      explanation: q.explanation || '',
      parts: q.parts || []
    };

    console.log(`✅ Successfully regenerated question ${questionIndex + 1}`);

    return transformedQuestion;

  } catch (error) {
    console.error('Question regeneration error:', error);
    throw new Error(`Failed to regenerate question: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Transform generated AI questions to ExamPlayer multi-part format
 */
export function transformToExamPlayerFormat(questions: GeneratedQuestion[]): any[] {
  return questions.map((q, index) => {
    if (q.type === 'multipart' && q.parts && q.parts.length > 0) {
      // Multi-part question format
      return {
        id: q.id,
        title: `Question ${index + 1}`,
        prompt: q.context || q.text,
        totalMarks: q.totalMarks || q.parts.reduce((sum, part) => sum + part.marks, 0),
        parts: q.parts.map(part => ({
          key: part.key,
          prompt: part.prompt,
          marks: part.marks,
          type: part.type === 'number' ? 'number' : 'text',
          answer: part.answer,
          numeric: part.numeric
        }))
      };
    } else {
      // Convert regular questions to single-part format for consistency
      return {
        id: q.id,
        title: `Question ${index + 1}`,
        prompt: q.text,
        totalMarks: q.points || 1,
        parts: [{
          key: 'a',
          prompt: q.type === 'mcq' ? 'Choose the correct answer:' : 'Enter your answer:',
          marks: q.points || 1,
          type: q.type === 'mcq' ? 'mcq' : 'text',
          answer: q.correctAnswer,
          choices: q.choices
        }]
      };
    }
  });
}

/**
 * Transform AI-generated questions to our quiz format
 */
export function transformToQuizFormat(questions: GeneratedQuestion[]): Array<{
  id: string;
  type?: string; // ✅ FIX: Include question type
  stem: string;
  options: string[];
  correctIndex: number;
  explanation?: string;
  secondsPerQuestion?: number;
}> {
  return questions.map(q => {
    // Handle multiple choice questions (normalized with hyphens)
    if ((q.type === 'multiple-choice' || q.type === 'multiple-select') && q.choices) {
      const correctIndex = q.choices.findIndex(choice => choice.isCorrect);

      return {
        id: q.id,
        type: q.type, // ✅ FIX: Preserve question type
        stem: q.text,
        options: q.choices.map(choice => choice.text),
        correctIndex: Math.max(0, correctIndex), // Fallback to 0 if no correct answer found
        explanation: q.explanation || '', // Use AI-generated explanation
        secondsPerQuestion: 60
      };
    }
    // Handle true/false questions (including auto-detected ones)
    else if (q.type === 'true-false' ||
             q.correctAnswer === true || q.correctAnswer === false ||
             q.correctAnswer === 'True' || q.correctAnswer === 'False' ||
             q.correctAnswer === 'true' || q.correctAnswer === 'false') {
      const isTrue = q.correctAnswer === true || q.correctAnswer === 'True' || q.correctAnswer === 'true';
      return {
        id: q.id,
        type: q.type || 'true-false', // ✅ FIX: Preserve or default to true-false
        stem: q.text,
        options: ['True', 'False'],
        correctIndex: isTrue ? 0 : 1,
        correctAnswer: isTrue, // ✅ PR-GRADING-1: Preserve boolean for grading compatibility
        explanation: q.explanation || '',
        secondsPerQuestion: 45 // Less time for true/false
      };
    }
    // Handle matching questions
    else if (q.type === 'matching' && q.choices) {
      return {
        id: q.id,
        type: q.type, // ✅ FIX: Preserve question type
        stem: q.text,
        options: q.choices.map(choice => choice.text), // Definitions to match against
        correctIndex: 0, // Not used for matching questions
        explanation: q.correctAnswer || '', // Contains matching pairs format
        secondsPerQuestion: 120 // More time for matching
      };
    }
    // Handle fill-in-the-blank questions (normalized with hyphens)
    else if (q.type === 'fill-blank') {
      return {
        id: q.id,
        type: q.type, // ✅ FIX: Preserve question type
        stem: q.text,
        options: ['Fill in the blank'], // Placeholder for fill-in-blank
        correctIndex: 0,
        explanation: q.correctAnswer || q.explanation || '',
        secondsPerQuestion: 90 // Medium time for fill-in-blank
      };
    }
    // Handle essay questions (normalized)
    else if (q.type === 'essay') {
      return {
        id: q.id,
        type: q.type, // ✅ FIX: Preserve question type
        stem: q.text,
        options: ['Essay response'], // Placeholder for essay
        correctIndex: 0,
        explanation: q.correctAnswer || q.explanation || '',
        secondsPerQuestion: 180 // More time for essays
      };
    }
    // Handle short answer questions (normalized with hyphens)
    else if (q.type === 'short-answer') {
      return {
        id: q.id,
        type: q.type, // ✅ FIX: Preserve question type
        stem: q.text,
        options: ['Short answer response'], // Placeholder for short answer
        correctIndex: 0,
        explanation: q.correctAnswer || q.explanation || '',
        secondsPerQuestion: 120 // More time for short answers
      };
    }
    // Unknown type fallback
    else {
      console.warn(`[transformToQuizFormat] Unknown question type: ${q.type} for question ${q.id}`);
      return {
        id: q.id,
        type: q.type, // ✅ FIX: Preserve question type (even if unknown)
        stem: q.text,
        options: ['Response required'], // Generic placeholder
        correctIndex: 0,
        explanation: q.correctAnswer || q.explanation || '',
        secondsPerQuestion: 120
      };
    }
  });
}

/**
 * PR-GRADING-1: Validate questions for grading compatibility
 * Checks that objective questions have required answer keys
 * Returns array of validation errors (empty = valid)
 */
export function validateQuestionsForGrading(questions: any[]): Array<{
  questionIndex: number;
  questionId: string;
  questionType: string;
  issue: string;
  severity: 'error' | 'warning';
}> {
  const errors: Array<{
    questionIndex: number;
    questionId: string;
    questionType: string;
    issue: string;
    severity: 'error' | 'warning';
  }> = [];

  questions.forEach((q, index) => {
    const qId = q.id || `question_${index}`;
    const qType = q.type || 'unknown';

    // Multiple Choice validation
    if (qType === 'mcq' || qType === 'multiple_choice' || qType === 'multiple-choice') {
      if (!q.options || !Array.isArray(q.options) || q.options.length < 2) {
        errors.push({
          questionIndex: index,
          questionId: qId,
          questionType: qType,
          issue: 'Multiple choice question must have at least 2 options',
          severity: 'error'
        });
      }

      const hasCorrectIndex = typeof q.correctIndex === 'number';
      const hasCorrectAnswerIndex = typeof q.correctAnswerIndex === 'number';

      if (!hasCorrectIndex && !hasCorrectAnswerIndex) {
        errors.push({
          questionIndex: index,
          questionId: qId,
          questionType: qType,
          issue: 'Multiple choice question missing correctIndex or correctAnswerIndex',
          severity: 'error'
        });
      } else {
        const correctIdx = q.correctIndex ?? q.correctAnswerIndex;
        if (correctIdx < 0 || correctIdx >= (q.options?.length || 0)) {
          errors.push({
            questionIndex: index,
            questionId: qId,
            questionType: qType,
            issue: `correctIndex ${correctIdx} is out of range for ${q.options?.length || 0} options`,
            severity: 'error'
          });
        }
      }
    }

    // True/False validation
    if (qType === 'true_false' || qType === 'true-false') {
      const hasCorrectAnswer = typeof q.correctAnswer === 'boolean' ||
                               typeof q.correctAnswer === 'string';
      const hasCorrectIndex = typeof q.correctIndex === 'number';
      const hasCorrectAnswerIndex = typeof q.correctAnswerIndex === 'number';

      if (!hasCorrectAnswer && !hasCorrectIndex && !hasCorrectAnswerIndex) {
        errors.push({
          questionIndex: index,
          questionId: qId,
          questionType: qType,
          issue: 'True/False question missing correctAnswer (boolean) or correctIndex (0/1)',
          severity: 'error'
        });
      }

      // Validate options format
      if (!q.options || !Array.isArray(q.options) || q.options.length !== 2) {
        errors.push({
          questionIndex: index,
          questionId: qId,
          questionType: qType,
          issue: 'True/False question should have exactly 2 options: ["True", "False"]',
          severity: 'warning'
        });
      }
    }

    // Short answer validation
    if (qType === 'short_answer' || qType === 'short-answer') {
      const hasCorrectAnswer = q.correctAnswer || q.correctAnswers;
      if (!hasCorrectAnswer) {
        // This is just a warning - short answers may require manual grading
        errors.push({
          questionIndex: index,
          questionId: qId,
          questionType: qType,
          issue: 'Short answer question has no correctAnswer - will require manual grading',
          severity: 'warning'
        });
      }
    }

    // Fill blank validation
    if (qType === 'fill_blank' || qType === 'fill-blank') {
      if (!q.explanation && !q.correctAnswer) {
        errors.push({
          questionIndex: index,
          questionId: qId,
          questionType: qType,
          issue: 'Fill blank question has no correctAnswer - will require manual grading',
          severity: 'warning'
        });
      }
    }

    // Essay validation (always manual)
    if (qType === 'essay') {
      // Essays are always manual, no validation needed
    }
  });

  return errors;
}